import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';
import React, {Fragment} from 'react';
import SelectAllIcon from '@mui/icons-material/PlaylistAdd';
import Tooltip from '@mui/material/Tooltip';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';

import {LABS_MAP} from '@explorer/constants';
import {
  addResultToOrder,
  addResultToView,
  removeResultFromOrder,
  removeResultFromView,
} from '@explorer/actions/results';
import {focusItems, unfocusItems} from '@explorer/actions/focused-item';
import {getUniqueId} from '@explorer/util/items';
import {setActiveResultId} from '@explorer/actions/sidebar';
import {trackEvent} from '@explorer/analytics';

// NOTE: Now only shows for active results
const BreadcrumbsSection = ({activeResult, ...props}) => {
  const {t} = useTranslation();
  const resetSelectedResult = () => {
    props.setActiveResultId(null);
  };
  const sceneIds = activeResult && activeResult.properties.itemIds;

  const viewFullySelected =
    sceneIds && sceneIds.every(id => props.viewItems.includes(id));

  const orderFullySelected =
    sceneIds && sceneIds.every(id => props.orderItems.includes(id));

  const footprintFullySelected =
    sceneIds &&
    sceneIds.every(id =>
      props.focusedItems.map(item => getUniqueId(item)).includes(id)
    );

  const fullySelected = props.useListv2
    ? viewFullySelected || orderFullySelected || footprintFullySelected
    : viewFullySelected || orderFullySelected;

  const onSelectAllClick = () => {
    if (fullySelected) {
      trackEvent('Clicked Deselect All on breadcrumbs', {
        previewMode: props.useListv2,
      });
      props.removeResultFromView({resultId: activeResult.id});
      props.removeResultFromOrder({resultId: activeResult.id});
      props.useListv2 &&
        props.unfocusItems({items: activeResult.properties.items});
    } else {
      trackEvent('Clicked Select All on breadcrumbs', {
        previewMode: props.useListv2,
      });
      props.addResultToView({resultId: activeResult.id});
      props.addResultToOrder({resultId: activeResult.id});
      props.useListv2 &&
        props.focusItems({items: activeResult.properties.items});
    }
  };

  return (
    <Fragment>
      <Box
        flexGrow={1}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        aria-label="results-breadcrumbs"
        mr={1}
      >
        {activeResult && (
          <Fragment>
            <Tooltip title={t('resultsList.tooltipBackToResults')}>
              <Button
                size="small"
                color="inherit"
                onClick={resetSelectedResult}
                startIcon={<ArrowBackIcon />}
                data-qe="results-breadcrumb-back"
              >
                {props.useListv2
                  ? t('common:date.long', {
                      date: activeResult.properties.acquired,
                    })
                  : t('resultsList.buttonBackToResults')}
              </Button>
            </Tooltip>

            <Tooltip
              title={
                fullySelected
                  ? t('resultsList.buttonDeselectAll')
                  : t('resultsList.buttonSelectAll')
              }
            >
              <IconButton
                size="small"
                onClick={onSelectAllClick}
                data-qe={`list-tabs-${
                  fullySelected ? 'deselect' : 'select'
                }-all`}
                color={fullySelected ? 'inherit' : 'primary'}
              >
                <Icon
                  component={fullySelected ? ClearAllIcon : SelectAllIcon}
                />
              </IconButton>
            </Tooltip>
          </Fragment>
        )}
      </Box>
    </Fragment>
  );
};

BreadcrumbsSection.propTypes = {
  activeResult: PropTypes.object,
  addResultToOrder: PropTypes.func,
  addResultToView: PropTypes.func,
  count: PropTypes.number.isRequired,
  orderItems: PropTypes.array,
  focusedItems: PropTypes.array,
  removeResultFromOrder: PropTypes.func,
  removeResultFromView: PropTypes.func,
  setActiveResultId: PropTypes.func,
  useListv2: PropTypes.bool,
  viewItems: PropTypes.array,
  focusItems: PropTypes.func,
  unfocusItems: PropTypes.func,
};

const mapStateToProps = state => ({
  activeResult: state.results.resultsById[state.sidebar.activeResultId],
  orderItems: state.results.orderItems,
  useListv2: !state.labs[LABS_MAP.SINGLE_ACTION],
  viewItems: state.results.viewItems,
  focusedItems: state.focusedItems,
});

const mapDispatchProps = {
  addResultToOrder,
  addResultToView,
  removeResultFromOrder,
  removeResultFromView,
  setActiveResultId,
  unfocusItems,
  focusItems,
};

export default connect(mapStateToProps, mapDispatchProps)(BreadcrumbsSection);
